import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { Button, DatePicker, Form, Input, Modal, Select, Space, Tag, Tooltip, message } from 'antd';
import axiosClient from 'api/axiosClient';
import { selectOvertimeData } from 'features/overtime/overtimeSlice';
import {
  updateOvertimePayrunMonth,
  getOvertimeByPayrunMonth,
  updateSingleOvertimePayrunMonth,
  getOvertimeAllType,
  getOvertimeTypeForSalaryCoefficient,
  getAllOvertimeRecords,
} from 'features/overtime/overtimeAction';
import PageHeader from 'components/PageHeader';
import moment from 'moment';
import { getUser } from 'utils/auth';
import ITVSearch from 'components/ITVSearch';
import { CalendarOutlined, CheckCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import ITVTable from 'components/ITVTable';
import { ContainerMain } from '../historyOTApproval';
import { useSelector } from 'react-redux';
const Container = styled.div`
  padding: 20px;
  background-color: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const PayrunManagementContainer = styled.div`
  margin-top: 20px;
`;

const SearchContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
`;

// Enum for modal modes
const MODE = {
  ADD: 'add',
  EDIT: 'edit',
  VIEW: 'view',
};

const OvertimePayrunManagement = () => {
  const { t } = useTranslation(['menu', 'action', 'overtime', 'timesheet']);
  const dispatch = useAppDispatch();
  const user = getUser() || {};
  const userId = user?.id;

  const overtimeData = useAppSelector(selectOvertimeData);
  const {
    overtimeRequestList,
    loadingOvertimeRequestList,
    overtimeByPayrunMonth,
    loadingOvertimeByPayrunMonth,
    allOvertimeRecords,
    loadingAllOvertimeRecords,
    payrunMonthUpdateLoading,
  } = overtimeData;

  // State for payrun management
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [isCheckedAll, setIsCheckedAll] = useState<boolean>(false);
  const [currentItem, setCurrentItem] = useState<any>(null);
  const [searchText, setSearchText] = useState('');
  const [payrunMonth, setPayrunMonth] = useState<string>(moment().format('YYYY-MM'));
  const [viewMode, setViewMode] = useState<'all' | 'byPayrun'>('all');
  const [filterMode, setFilterMode] = useState<'single' | 'range'>('range'); // Default to range
  const [rangeStartDate, setRangeStartDate] = useState<string | null>(null);
  const [rangeEndDate, setRangeEndDate] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  // Use 30 as default page size for range mode to match previous behavior
  const [pageSize, setPageSize] = useState(30);
  const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
  const [assignPayrunMonth, setAssignPayrunMonth] = useState<string>(moment().format('YYYY-MM'));
  const [assignSelectedRecords, setAssignSelectedRecords] = useState<any[]>([]);

  // State for modal and request management
  const [mode, setMode] = useState('');
  const [sizePage, setSizePage] = useState(30);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [form] = Form.useForm();

  // State for applicable salary coefficient and available coefficients
  const [applicableSalaryCoefficient, setApplicableSalaryCoefficient] = useState<number | null>(
    null,
  );
  const [availableSalaryCoefficients, setAvailableSalaryCoefficients] = useState<number[]>([]);
  const [salaryCoefficientsWithTimeFrame, setSalaryCoefficientsWithTimeFrame] = useState<
    Array<{ coefficient: number; timeFrame: string }>
  >([]);

  // Get overtime type data from Redux store
  const {
    overtimeTypeData,
    overtimeTypeForSalaryCoefficient,
    loadingOvertimeTypeForSalaryCoefficient,
  } = useSelector((state: any) => state.overtime);

  // Title for the header
  const titleHeader = t('menu:overtimePayrunManagement');

  // Fetch overtime data when component mounts or pagination changes
  useEffect(() => {
    if (userId) {
      if (viewMode === 'all') {
        // Use the main endpoint to get all overtime records for "All OT" view
        // Only show approved records that have not been forwarded (payrunMonth is null)
        dispatch(
          getAllOvertimeRecords({
            page: currentPage,
            pageSize: pageSize,
            status: 'approved', // Only show approved records
            payrunMonthIsNull: true, // Only show records without payrunMonth assigned
          }),
        );
      } else {
        // Handle byPayrun mode with different filter modes
        if (filterMode === 'single') {
          dispatch(
            getOvertimeByPayrunMonth({
              payrunMonth: payrunMonth || moment().format('YYYY-MM'),
              page: currentPage,
              pageSize: pageSize,
            }),
          );
        } else {
          // Range mode - if both dates are selected, use range filter
          if (rangeStartDate && rangeEndDate) {
            dispatch(
              getOvertimeByPayrunMonth({
                payrunMonth: rangeStartDate,
                toMonth: rangeEndDate,
                page: currentPage,
                pageSize: pageSize,
              }),
            );
          } else {
            // If no range is selected, show all forwarded records (records with non-null payrun_month)
            // For range default mode, we want to show all forwarded records
            dispatch(
              getOvertimeByPayrunMonth({
                payrunMonth: 'range', // Use range mode to show forwarded records
                toMonth: '', // Empty value will trigger backend to return all forwarded records
              }),
            );
          }
        }
      }
    }
    // Fetch all overtime types for salary coefficient calculation
    dispatch(
      getOvertimeAllType({
        data: { limit: 100, offset: 0 },
      }),
    );
  }, [
    dispatch,
    userId,
    viewMode,
    payrunMonth,
    filterMode,
    rangeStartDate,
    rangeEndDate,
    currentPage,
    pageSize,
  ]);

  // Debug: Log the API response data
  useEffect(() => {
    if (viewMode === 'byPayrun' && overtimeByPayrunMonth?.length > 0) {
      console.log('Overtime by payrun month data:', overtimeByPayrunMonth);
    }
  }, [overtimeByPayrunMonth, viewMode]);

  const filteredData = React.useMemo(() => {
    // Sử dụng data khác nhau tùy theo viewMode
    let data = [];
    // Since we removed allMonths view mode, we only use overtimeByPayrunMonth for byPayrun mode
    // and allOvertimeRecords for all mode
    if (viewMode === 'all') {
      data = Array.isArray(allOvertimeRecords) ? allOvertimeRecords : [];
    } else {
      data = Array.isArray(overtimeByPayrunMonth) ? overtimeByPayrunMonth : [];
    }
    if (!searchText)
      return data.filter((item) => item.status === 'approved' && item.totalHour !== 0);

    const searchTerms = searchText
      .toLowerCase()
      .split(' ')
      .filter((term) => term);

    return data.filter((item: any) => {
      if (item.totalHour === 0) return false;

      // Check if all search terms match at least one field
      return searchTerms.every((term) => {
        // Extract year and month from startTime for date-based searching
        const startTimeDate = item?.startTime ? moment(item.startTime).format('YYYY-MM') : '';

        return (
          // Search in projectCode
          item?.projectCode?.toLowerCase().includes(term) ||
          // Search in reason
          item?.reason?.toLowerCase().includes(term) ||
          // Search in name
          item?.name?.toLowerCase().includes(term) ||
          // Search in employeeCode
          item?.employeeCode?.toLowerCase().includes(term) ||
          // Search in payrunMonth (if exists)
          (item?.payrunMonth && item?.payrunMonth.toLowerCase().includes(term)) ||
          // Search in startTime date (formatted as YYYY-MM)
          startTimeDate.includes(term)
        );
      });
    });
  }, [overtimeByPayrunMonth, allOvertimeRecords, searchText, viewMode]);

  // Handle search text change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  // Handle search from ITVSearch component
  const search = (value: string) => {
    setSearchText(value);
  };

  // Handle view overtime request
  const handleView = (record: any) => {
    setCurrentItem(record);
    setMode(MODE.VIEW);
  };

  // Find applicable salary coefficients based on overtime type and time frames
  const findSalaryCoefficients = (startTime: string, endTime: string, overtimeType: string) => {
    if (
      !overtimeTypeForSalaryCoefficient ||
      overtimeTypeForSalaryCoefficient.length === 0 ||
      !startTime ||
      !endTime ||
      !overtimeType
    ) {
      return { applicable: null, available: [], withTimeFrame: [] };
    }

    console.log('Finding salary coefficient for:', { startTime, endTime, overtimeType });
    console.log('Available overtime type data:', overtimeTypeForSalaryCoefficient);

    // Convert start and end time to moment objects for comparison
    const startMoment = moment(startTime);
    const endMoment = moment(endTime);

    // Array to store all applicable coefficients
    const applicableCoefficients = [];
    const coefficientsWithTimeFrame = [];
    const coefficientSet = new Set(); // To avoid duplicates

    // Find all applicable coefficients based on time frame overlap
    for (const item of overtimeTypeForSalaryCoefficient) {
      const frameStart = moment(item.timeFrameStart, 'HH:mm');
      const frameEnd = moment(item.timeFrameEnd, 'HH:mm');
      const userStart = moment(startMoment.format('HH:mm'), 'HH:mm');
      const userEnd = moment(endMoment.format('HH:mm'), 'HH:mm');

      console.log('Checking time frame:', {
        frameStart: frameStart.format('HH:mm'),
        frameEnd: frameEnd.format('HH:mm'),
        userStart: userStart.format('HH:mm'),
        userEnd: userEnd.format('HH:mm'),
        salaryCoefficient: item.salaryCoefficient,
      });

      // Check if there's any overlap between user time and frame time
      // Overlap exists if: userStart < frameEnd AND userEnd > frameStart
      if (userStart.isBefore(frameEnd) && userEnd.isAfter(frameStart)) {
        console.log('Found overlapping time frame with coefficient:', item.salaryCoefficient);
        if (!coefficientSet.has(item.salaryCoefficient)) {
          coefficientSet.add(item.salaryCoefficient);
          applicableCoefficients.push(item.salaryCoefficient);
          coefficientsWithTimeFrame.push({
            coefficient: item.salaryCoefficient,
            timeFrame: `${frameStart.format('HH:mm')} - ${frameEnd.format('HH:mm')}`,
          });
        }
      }
    }

    // Sort coefficients in ascending order
    applicableCoefficients.sort((a, b) => a - b);
    coefficientsWithTimeFrame.sort((a, b) => a.coefficient - b.coefficient);

    console.log('All applicable coefficients:', applicableCoefficients);
    console.log('Coefficients with time frame:', coefficientsWithTimeFrame);

    return {
      applicable: applicableCoefficients.length > 0 ? applicableCoefficients[0] : null, // Default to first (lowest) coefficient
      available: applicableCoefficients,
      withTimeFrame: coefficientsWithTimeFrame,
    };
  };

  // Handle open edit modal
  const handleOpenEditModal = (record: any) => {
    setCurrentItem(record);
    // Reset form and states first to clear any previous values
    form.resetFields();
    setApplicableSalaryCoefficient(null);
    setAvailableSalaryCoefficients([]);
    setSalaryCoefficientsWithTimeFrame([]);

    // First, fetch overtime type data for salary coefficient calculation
    dispatch(
      getOvertimeTypeForSalaryCoefficient({
        type: record?.type || 'weekday',
      }),
    );

    // Set form values that don't depend on the salary coefficient data
    const payrunMonthValue = record.payrunMonth
      ? moment(record.payrunMonth, 'YYYY-MM')
      : moment(record.startTime).isValid()
      ? moment(record.startTime)
      : moment();

    form.setFieldsValue({
      employeeCode: record.employeeCode || '',
      name: record.name || '',
      payrunMonth: payrunMonthValue,
      transferHours: record.totalHour || 0,
      totalHour: record.totalHour || 0,
      salaryCoefficient: null, // Will be set after data is loaded
    });

    setIsEditModalVisible(true);
  };

  // Effect to handle salary coefficient data when it's loaded
  useEffect(() => {
    if (!isEditModalVisible || !currentItem || !overtimeTypeForSalaryCoefficient) return;

    // Find applicable salary coefficient and available options
    const { applicable, available, withTimeFrame } = findSalaryCoefficients(
      currentItem?.startTime,
      currentItem?.endTime,
      currentItem?.type || 'weekday',
    );

    console.log('Applicable salary coefficient:', applicable);
    console.log('Available salary coefficients:', available);
    console.log('Salary coefficients with time frame:', withTimeFrame);

    setApplicableSalaryCoefficient(applicable);
    setAvailableSalaryCoefficients(available);
    setSalaryCoefficientsWithTimeFrame(withTimeFrame);

    // Update the form with the salary coefficient
    // Make sure to use the same type as the form field expects
    form.setFieldsValue({
      salaryCoefficient: applicable !== null ? applicable.toString() : null,
    });

    // Debug log to check the form values after setting
    form.validateFields(['salaryCoefficient']).then((values) => {
      console.log('Form values after setting salary coefficient:', {
        salaryCoefficient: values.salaryCoefficient,
        type: typeof values.salaryCoefficient,
      });
    });
  }, [overtimeTypeForSalaryCoefficient, isEditModalVisible, currentItem]);

  // Handle save payrun month
  const handleSavePayrunMonth = async () => {
    console.log('currentItem.selectedIds:', currentItem?.selectedIds);
    console.log('⚠️ FINAL payload to API:', {
      mode: currentItem?.isEditAll ? 'BULK' : 'SINGLE',
      selectedIds: currentItem?.selectedIds,
      currentItem: currentItem,
    });

    try {
      const values = await form.validateFields();

      if (!currentItem) return;

      // Ensure we have a valid moment object
      const payrunMonthMoment = moment.isMoment(values.payrunMonth)
        ? values.payrunMonth.clone() // Use .clone() if you need a copy
        : moment(values.payrunMonth, 'YYYY-MM');

      if (!payrunMonthMoment.isValid()) {
        message.error('Invalid date format');
        return;
      }

      const formattedPayrunMonth = payrunMonthMoment.format('YYYY-MM');

      // Additional validation for transfer hours
      const transferHours = parseFloat(values.transferHours || '0');
      const totalHours = parseFloat(currentItem?.totalHour || '0');

      if (transferHours < 0) {
        message.error('Transfer hours cannot be negative');
        return;
      }

      if (transferHours === 0) {
        message.error('Transfer hours cannot be zero. Leave empty to transfer all hours.');
        return;
      }

      if (transferHours < 0.01) {
        message.error('Transfer hours must be at least 0.01 hours');
        return;
      }

      if (transferHours > totalHours) {
        message.error(
          `Transfer hours (${transferHours}) cannot exceed total hours (${totalHours})`,
        );
        return;
      }

      // Callback function to execute after successful update
      const callBackFunction = () => {
        setIsEditModalVisible(false);
        form.resetFields();
        setCurrentItem(null);
        // Refresh data after update
        if (viewMode === 'all') {
          dispatch(
            getAllOvertimeRecords({
              page: currentPage,
              pageSize: pageSize,
              status: 'approved', // Only show approved records
              payrunMonthIsNull: true, // Only show records without payrunMonth assigned
            }),
          );
        } else {
          // Handle byPayrun mode
          if (filterMode === 'single') {
            dispatch(
              getOvertimeByPayrunMonth({
                payrunMonth: payrunMonth || moment().format('YYYY-MM'),
                page: currentPage,
                pageSize: pageSize,
              }),
            );
          } else {
            // Range mode - if both dates are selected, use range filter
            if (rangeStartDate && rangeEndDate) {
              dispatch(
                getOvertimeByPayrunMonth({
                  payrunMonth: 'range', // Placeholder value
                  fromMonth: rangeStartDate,
                  toMonth: rangeEndDate,
                  page: currentPage,
                  pageSize: pageSize,
                }),
              );
            } else {
              // If no range is selected, show all records with non-null payrun_month
              // For range default mode, we want to show all records, so we don't pass pagination params
              dispatch(
                getOvertimeByPayrunMonth({
                  payrunMonth: 'null', // Show all records with non-null payrun_month when no range is selected
                  fromMonth: '', // Empty values will trigger backend to return all records
                  toMonth: '', // Empty values will trigger backend to return all records
                }),
              );
            }
          }
        }
        message.success(t('overtime:updatePayrunMonthSuccess'));
      };

      // Kiểm tra xem đang cập nhật một bản ghi hay nhiều bản ghi đã chọn
      if (currentItem.isEditAll && currentItem.selectedIds && currentItem.selectedIds.length > 0) {
        // For bulk edit, we want to update each record individually with the same values
        // This is similar to how the individual edit works but applied to multiple records
        const transferHours = parseFloat(values.transferHours || '0');
        const originalTotalHours = parseFloat(values.totalHour || '0');
        const selectedSalaryCoefficient =
          values.salaryCoefficient !== undefined && values.salaryCoefficient !== null
            ? Number(values.salaryCoefficient)
            : null;

        // Kiểm tra transferHours không lớn hơn totalHour
        if (transferHours > originalTotalHours) {
          message.error(t('overtime:transferHoursCannotExceedTotalHours'));
          return;
        }

        // For bulk update, use the bulk update action
        console.log(
          `Bulk update: ${currentItem.selectedIds.length} records, transferHours: ${transferHours}, totalHours: ${originalTotalHours}`,
        );

        dispatch(
          updateOvertimePayrunMonth({
            overtimeIds: currentItem.selectedIds,
            payrunMonth: formattedPayrunMonth,
            transferHours: transferHours,
            salaryCoefficient: selectedSalaryCoefficient,
            callBack: callBackFunction,
          }),
        );
      } else {
        // Cập nhật một bản ghi duy nhất
        // Khi chỉnh sửa một bản ghi, người dùng có thể nhập transferHours
        // và chúng ta cần kiểm tra transferHours <= totalHour
        const transferHours = parseFloat(values.transferHours || '0');
        const originalTotalHours = parseFloat(values.totalHour || '0');

        // Get the selected salary coefficient and ensure it's a number
        // The value might be a string from the form, so we need to convert it to a number
        const selectedSalaryCoefficient =
          values.salaryCoefficient !== undefined && values.salaryCoefficient !== null
            ? Number(values.salaryCoefficient)
            : null;

        console.log('Selected salary coefficient (parsed):', {
          rawValue: values.salaryCoefficient,
          parsedValue: selectedSalaryCoefficient,
          type: typeof selectedSalaryCoefficient,
        });

        // Kiểm tra transferHours không lớn hơn totalHour
        if (transferHours > originalTotalHours) {
          message.error(t('overtime:transferHoursCannotExceedTotalHours'));
          return;
        }

        // Debug logs
        console.log('Sending data to API for single record:', {
          id: currentItem.id,
          payrunMonth: formattedPayrunMonth,
          transferHours: transferHours,
          salaryCoefficient: selectedSalaryCoefficient,
          originalTotalHours: originalTotalHours,
          formValues: values,
          currentItem: currentItem,
        });

        // Check if transferHours is different from totalHour
        if (Math.abs(transferHours - originalTotalHours) < 0.001) {
          console.log('Transferring all hours');
        } else {
          console.log(`Transferring ${transferHours} out of ${originalTotalHours} hours`);
        }

        dispatch(
          updateSingleOvertimePayrunMonth({
            id: currentItem.id,
            payrunMonth: formattedPayrunMonth,
            transferHours: transferHours,
            salaryCoefficient: selectedSalaryCoefficient,
            callBack: callBackFunction,
          }),
        );
      }
    } catch (error) {
      console.error('Error updating payrun month:', error);

      // Enhanced error handling with specific error messages
      if (error?.response?.data?.message) {
        message.error(`Update failed: ${error.response.data.message}`);
      } else if (error?.message) {
        message.error(`Update failed: ${error.message}`);
      } else {
        message.error('Failed to update payrun month. Please try again.');
      }

      // Log detailed error for debugging
      console.error('Detailed error:', {
        error,
        currentItem,
        formValues: form.getFieldsValue(),
      });
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditModalVisible(false);
    setCurrentItem(null);
    form.resetFields();
  };

  // Handle add new overtime request
  const onAdd = () => {
    setMode(MODE.ADD);
  };

  // Handle save overtime request
  const onSave = () => {
    setMode('');
    // Refresh data after save
    if (viewMode === 'all') {
      dispatch(
        getAllOvertimeRecords({
          page: currentPage,
          pageSize: pageSize,
          status: 'approved', // Only show approved records
          payrunMonthIsNull: true, // Only show records without payrunMonth assigned
        }),
      );
    } else {
      dispatch(
        getOvertimeByPayrunMonth({
          payrunMonth,
          page: currentPage,
          pageSize: pageSize,
        }),
      );
    }
  };

  // Handle payrun month change for the filter
  const handlePayrunMonthChange = (date: any, dateString: string) => {
    if (dateString) {
      setPayrunMonth(dateString);
      if (viewMode === 'byPayrun' && filterMode === 'single') {
        dispatch(
          getOvertimeByPayrunMonth({
            payrunMonth: dateString,
            page: currentPage,
            pageSize: pageSize,
          }),
        );
      }
    }
  };

  // Handle filter mode change
  const handleFilterModeChange = (mode: 'single' | 'range') => {
    setFilterMode(mode);
    // Reset range dates when switching modes
    if (mode === 'single') {
      setRangeStartDate(null);
      setRangeEndDate(null);
    } else {
      setRangeStartDate(null);
      setRangeEndDate(null);
    }

    // Refresh data based on new filter mode
    if (viewMode === 'byPayrun') {
      if (mode === 'single') {
        dispatch(
          getOvertimeByPayrunMonth({
            payrunMonth: payrunMonth || moment().format('YYYY-MM'),
            page: currentPage,
            pageSize: pageSize,
          }),
        );
      } else {
        // In range mode, if both dates are selected, use range filter
        if (rangeStartDate && rangeEndDate) {
          dispatch(
            getOvertimeByPayrunMonth({
              payrunMonth: 'range', // Placeholder value
              fromMonth: rangeStartDate,
              toMonth: rangeEndDate,
              page: currentPage,
              pageSize: pageSize,
            }),
          );
        } else {
          // If no range is selected, show all records by default
          // For range default mode, we want to show all records, so we don't pass pagination params
          dispatch(
            getOvertimeByPayrunMonth({
              payrunMonth: 'null',
            }),
          );
        }
      }
    }
  };

  // Handle range start date change
  const handleRangeStartDateChange = (date: any, dateString: string) => {
    setRangeStartDate(dateString);
    if (viewMode === 'byPayrun' && filterMode === 'range' && dateString && rangeEndDate) {
      dispatch(
        getOvertimeByPayrunMonth({
          payrunMonth: dateString,
          toMonth: rangeEndDate,
          page: currentPage,
          pageSize: pageSize,
        }),
      );
    }
  };

  // Handle range end date change
  const handleRangeEndDateChange = (date: any, dateString: string) => {
    setRangeEndDate(dateString);
    if (viewMode === 'byPayrun' && filterMode === 'range' && rangeStartDate && dateString) {
      dispatch(
        getOvertimeByPayrunMonth({
          payrunMonth: rangeStartDate,
          toMonth: dateString,
          page: currentPage,
          pageSize: pageSize,
        }),
      );
    }
  };

  // Handle payrun month change for the modal (doesn't trigger data fetching)
  // const handleModalPayrunMonthChange = (date: any) => {
  //   // Just update the form field, no need to fetch data or update global state
  //   form.setFieldsValue({ payrunMonth: date });
  // };

  // Handle assigning payrun month to selected overtimes
  const handleAssignPayrunMonth = () => {
    let selectedIds = [...selectedRowKeys];

    if (isCheckedAll && selectedRowKeys.length === 0) {
      selectedIds = filteredData.filter((item: any) => !item.disabled).map((item: any) => item.id);
    }

    if (selectedIds.length === 0) {
      message.warning(t('overtime:pleaseSelectRecordsToEdit'));
      return;
    }

    const records = filteredData.filter((item: any) => selectedIds.includes(item.id));

    setAssignSelectedRecords(records);

    const defaultMonth = moment(payrunMonth, 'YYYY-MM', true).isValid()
      ? moment(payrunMonth, 'YYYY-MM').format('YYYY-MM')
      : moment().format('YYYY-MM');

    setAssignPayrunMonth(defaultMonth);
    setIsAssignModalVisible(true);
  };

  const handleAssignModalCancel = () => {
    setIsAssignModalVisible(false);
    setAssignSelectedRecords([]);
  };

  const handleAssignModalOk = () => {
    const isValidMonth = assignPayrunMonth && moment(assignPayrunMonth, 'YYYY-MM', true).isValid();

    if (!isValidMonth) {
      message.error(t('overtime:payrunMonthRequired'));
      return;
    }

    const overtimeIds = assignSelectedRecords.map((item: any) => item.id);

    if (overtimeIds.length === 0) {
      message.warning(t('overtime:pleaseSelectRecordsToEdit'));
      return;
    }

    dispatch(
      updateOvertimePayrunMonth({
        overtimeIds,
        payrunMonth: assignPayrunMonth,
        callBack: () => {
          setSelectedRowKeys([]);
          setIsCheckedAll(false);
          setAssignSelectedRecords([]);
          setIsAssignModalVisible(false);

          if (viewMode === 'all') {
            dispatch(
              getAllOvertimeRecords({
                page: currentPage,
                pageSize: pageSize,
                status: 'approved', // Only show approved records
                payrunMonthIsNull: true, // Only show records without payrunMonth assigned
              }),
            );
          } else if (filterMode === 'single') {
            dispatch(
              getOvertimeByPayrunMonth({
                payrunMonth: payrunMonth || moment().format('YYYY-MM'),
                page: currentPage,
                pageSize: pageSize,
              }),
            );
          } else if (rangeStartDate && rangeEndDate) {
            dispatch(
              getOvertimeByPayrunMonth({
                payrunMonth: 'range',
                fromMonth: rangeStartDate,
                toMonth: rangeEndDate,
                page: currentPage,
                pageSize: pageSize,
              }),
            );
          } else {
            // For range default mode, we want to show all records, so we don't pass pagination params
            dispatch(
              getOvertimeByPayrunMonth({
                payrunMonth: 'null',
                fromMonth: '',
                toMonth: '',
              }),
            );
          }
        },
      }),
    );
  };

  // Handle payrun month change for the modal (doesn't trigger data fetching)
  const handleModalPayrunMonthChange = (date: any) => {
    // Just update the form field, no need to fetch data or update global state
    form.setFieldsValue({ payrunMonth: date });
  };

  // Handle opening the Edit All Payrun Month modal
  const handleOpenEditAllModal = () => {
    console.log('Before open modal - selectedRowKeys:', selectedRowKeys);
    console.log('Before open modal - isCheckedAll:', isCheckedAll);

    // Kiểm tra nếu không có hàng nào được chọn và không chọn tất cả
    if (!isCheckedAll && selectedRowKeys.length === 0) {
      message.warning(t('overtime:pleaseSelectRecordsToEdit'));
      return;
    }

    // Xác định danh sách ID cần cập nhật
    let selectedIds = [...selectedRowKeys]; // Create a copy to avoid reference issues

    // Chỉ khi không có ID nào được chọn cụ thể và isCheckedAll là true
    // thì mới lấy tất cả ID từ dữ liệu
    if (isCheckedAll && selectedRowKeys.length === 0) {
      console.log('Using all data because isCheckedAll is true and no specific rows selected');
      selectedIds = filteredData.filter((item: any) => !item.disabled).map((item: any) => item.id);
    }

    // For edit all functionality, we want to behave like individual edit but for multiple records
    // So we'll open the modal with the first selected record as reference
    const firstRecord =
      filteredData.find((item: any) => selectedIds.includes(item.id)) || filteredData[0];

    if (firstRecord) {
      // Reset form and states first to clear any previous values
      form.resetFields();
      setApplicableSalaryCoefficient(null);
      setAvailableSalaryCoefficients([]);
      setSalaryCoefficientsWithTimeFrame([]);

      // First, fetch overtime type data for salary coefficient calculation
      dispatch(
        getOvertimeTypeForSalaryCoefficient({
          type: firstRecord?.type || 'weekday',
        }),
      );

      // Set form values that don't depend on the salary coefficient data
      const payrunMonthValue = firstRecord.payrunMonth
        ? moment(firstRecord.payrunMonth, 'YYYY-MM')
        : moment(firstRecord.startTime).isValid()
        ? moment(firstRecord.startTime)
        : moment();

      form.setFieldsValue({
        employeeCode: firstRecord.employeeCode || '',
        name: firstRecord.name || '',
        payrunMonth: payrunMonthValue,
        transferHours: firstRecord.totalHour || 0,
        totalHour: firstRecord.totalHour || 0,
        salaryCoefficient: null, // Will be set after data is loaded
        isEditAll: true, // Mark as bulk edit
      });

      // Set current item with selected IDs for bulk update
      setCurrentItem({
        ...firstRecord,
        selectedIds: selectedIds, // Store selected IDs for bulk update
        isEditAll: true, // Mark as bulk edit
      });

      setIsEditModalVisible(true);
    }
  };

  // Handle switching view mode
  const handleViewModeChange = (mode: 'all' | 'byPayrun') => {
    setViewMode(mode);
    setSelectedRowKeys([]);
    setIsCheckedAll(false);
    setCurrentPage(1);

    // Reset filter mode and dates when changing view modes
    if (mode !== 'byPayrun') {
      setFilterMode('single');
      setRangeStartDate(null);
      setRangeEndDate(null);
    } else {
      // When switching to byPayrun, default to range mode
      setFilterMode('range');
      setRangeStartDate(null);
      setRangeEndDate(null);
    }

    // Tự động gọi API khi chuyển chế độ xem
    if (userId) {
      if (mode === 'all') {
        dispatch(
          getAllOvertimeRecords({
            page: currentPage,
            pageSize: pageSize,
            status: 'approved', // Only show approved records
            payrunMonthIsNull: true, // Only show records without payrunMonth assigned
          }),
        );
      } else {
        // Handle byPayrun mode with range as default
        if (filterMode === 'single' && mode === 'byPayrun') {
          dispatch(
            getOvertimeByPayrunMonth({
              payrunMonth: payrunMonth || moment().format('YYYY-MM'),
              page: currentPage,
              pageSize: pageSize,
            }),
          );
        } else if (filterMode === 'range' && mode === 'byPayrun') {
          // In range mode, always call with range parameters
          // When no dates are selected, we still pass the range parameters but with empty values
          // The backend will handle this by returning all records with non-null payrun_month
          // For range default mode, we want to show all records, so we don't pass pagination params
          dispatch(
            getOvertimeByPayrunMonth({
              payrunMonth: 'null', // Show all records with non-null payrun_month when no range is selected
              fromMonth: rangeStartDate || '', // Pass empty string when null
              toMonth: rangeEndDate || '', // Pass empty string when null
            }),
          );
        }
      }
    }
  };

  // Table columns
  const COLUMNS = [
    {
      title: t('overtime:projectName'),
      dataIndex: 'projectCode',
      key: 'projectCode',
      width: 100,
      minWidth: 100,
      render: (text: string, record: any) => <span>{text || '-'}</span>,
    },
    {
      title: t('overtime:employeeCode'),
      dataIndex: 'employeeCode',
      key: 'employeeCode',
      width: 100,
      minWidth: 100,
      align: 'center',
      render: (text: string, record: any) => <span>{text || '-'}</span>,
    },
    {
      title: t('overtime:name'),
      dataIndex: 'name',
      key: 'name',
      width: 100,
      minWidth: 100,
      render: (text: string, record: any) => <span>{text || '-'}</span>,
    },
    {
      title: t('overtime:reason'),
      dataIndex: 'reason',
      key: 'reason',
      width: 100,
      minWidth: 100,
      render: (text: string) => <span>{text || '-'}</span>,
    },
    {
      title: t('overtime:startTime'),
      dataIndex: 'startTime',
      key: 'startTime',
      width: 100,
      minWidth: 100,
      render: (text: string) => <span>{text ? moment(text).format('DD/MM/YYYY HH:mm') : '-'}</span>,
    },
    {
      title: t('overtime:endTime'),
      dataIndex: 'endTime',
      key: 'endTime',
      width: 100,
      minWidth: 100,
      render: (text: string) => <span>{text ? moment(text).format('DD/MM/YYYY HH:mm') : '-'}</span>,
    },
    {
      // Keep the column title as 'Total Hours' for both tabs
      title:
        viewMode === 'byPayrun' ? (
          <Tooltip
            title={
              t('overtime:transferredHoursTooltip') || 'Hours transferred to this payrun month'
            }
          >
            {t('overtime:totalHour')}
          </Tooltip>
        ) : (
          t('overtime:totalHour')
        ),
      dataIndex: 'totalHour',
      key: 'totalHour',
      width: 80,
      minWidth: 80,
      align: 'center',
      render: (text: number, record: any) => {
        // In both tabs, we show the totalHour value from the API response
        // In the byPayrun tab, this represents the hours transferred to this payrun month
        return <span>{text || 0}</span>;
      },
    },
    {
      title: t('overtime:payrunMonth'),
      dataIndex: 'payrunMonth',
      key: 'payrunMonth',
      width: 130,
      minWidth: 130,
      render: (text: string, record: any) => (
        <span>
          {text ? (
            <Tag color="green">
              <CalendarOutlined /> {text}
            </Tag>
          ) : (
            <Tag color="orange">
              {moment(record.startTime).format('YYYY-MM')}
              <Tooltip title={t('overtime:defaultPayrunMonth')}>
                <span> (Default)</span>
              </Tooltip>
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: t('overtime:action'),
      key: 'action',
      width: 80,
      minWidth: 80,
      align: 'center',
      render: (text: string, record: any) => (
        <Space>
          <Button
            type="link"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditModal(record);
            }}
          >
            {t('action:edit')}
          </Button>
        </Space>
      ),
    },
  ];

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      console.log('Selected keys changed:', selectedKeys);
      console.log('Is checked all:', isCheckedAll);
      setSelectedRowKeys(selectedKeys as number[]);
      // Kiểm tra nếu tất cả các hàng không bị disabled đều được chọn
      const allNonDisabledRows = filteredData.filter((i: any) => !i.disabled).length;
      if (selectedKeys.length === allNonDisabledRows && allNonDisabledRows > 0) {
        setIsCheckedAll(true);
      } else if (selectedKeys.length === 0) {
        setIsCheckedAll(false);
      }
    },
    getCheckboxProps: (record: any) => ({
      disabled: record.disabled,
    }),
    onSelectAll: (selected: boolean) => {
      console.log('Select all changed:', selected);
      setIsCheckedAll(selected);
    },
  };

  // Thêm tùy chọn mặc định với coefficient = 1
  const defaultCoefficientOption = {
    coefficient: 1,
    timeFrame: 'Thông thường',
    isDefault: true,
  };

  // Kết hợp mảng hiện có với tùy chọn mặc định và loại bỏ trùng lặp
  const allSalaryCoefficients = [
    defaultCoefficientOption,
    ...(salaryCoefficientsWithTimeFrame || []),
  ].filter(
    (item, index, self) => index === self.findIndex((t) => t.coefficient === item.coefficient),
  );

  // Sắp xếp lại mảng để đảm bảo tùy chọn mặc định luôn ở đầu
  allSalaryCoefficients.sort((a, b) => {
    if (a.coefficient === 1) return -1;
    if (b.coefficient === 1) return 1;
    return 0;
  });

  // Cập nhật điều kiện hiển thị thông báo
  const showTimeFrameHint = salaryCoefficientsWithTimeFrame.length > 1;
  const hasMultipleOptions = allSalaryCoefficients.length > 1;

  return (
    <ContainerMain>
      <Container>
        <PageHeader title={titleHeader} />
      </Container>
      <div style={{ backgroundColor: '#fff', padding: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
          <div>
            <Space>
              <ITVSearch
                handleSearch={(value: any) => search(value)}
                placeholder={t('action:search')}
                style={{ width: 250 }}
              />
              <Button
                type={viewMode === 'all' ? 'primary' : 'default'}
                onClick={() => handleViewModeChange('all')}
              >
                {t('overtime:allOvertime')}
              </Button>
              <Button
                type={viewMode === 'byPayrun' ? 'primary' : 'default'}
                onClick={() => handleViewModeChange('byPayrun')}
              >
                {t('overtime:byPayrunMonth')}
              </Button>
            </Space>
          </div>
          <div>
            <Space>
              {viewMode === 'byPayrun' && (
                <>
                  <Select
                    value={filterMode}
                    onChange={handleFilterModeChange}
                    style={{ width: 120 }}
                  >
                    <Select.Option value="single">{t('overtime:singleMonth')}</Select.Option>
                    <Select.Option value="range">{t('overtime:rangeMonth')}</Select.Option>
                  </Select>
                  {filterMode === 'single' ? (
                    <DatePicker
                      picker="month"
                      value={payrunMonth ? moment(payrunMonth) : null}
                      onChange={handlePayrunMonthChange}
                      format="YYYY-MM"
                    />
                  ) : (
                    <>
                      <DatePicker
                        picker="month"
                        placeholder={t('overtime:startDate')}
                        value={rangeStartDate ? moment(rangeStartDate) : null}
                        onChange={handleRangeStartDateChange}
                        format="YYYY-MM"
                      />
                      <span>-</span>
                      <DatePicker
                        picker="month"
                        placeholder={t('overtime:endDate')}
                        value={rangeEndDate ? moment(rangeEndDate) : null}
                        onChange={handleRangeEndDateChange}
                        format="YYYY-MM"
                      />
                    </>
                  )}
                </>
              )}
              {/* <Button
                type="default"
                onClick={handleOpenEditAllModal}
                disabled={!isCheckedAll && selectedRowKeys.length === 0}
                loading={payrunMonthUpdateLoading}
                icon={<CalendarOutlined />}
              >
                {t('overtime:editAllPayrunMonth')}
              </Button> */}
              <Button
                type="primary"
                onClick={handleAssignPayrunMonth}
                icon={<CheckCircleOutlined />}
                disabled={!isCheckedAll && selectedRowKeys.length === 0}
              >
                {t('overtime:assignPayrunMonth')}
              </Button>
            </Space>
          </div>
        </div>

        <ITVTable
          columns={COLUMNS}
          dataSource={filteredData.map((item: any) => ({
            key: item.id,
            ...item,
          }))}
          data={filteredData}
          loading={viewMode === 'all' ? loadingAllOvertimeRecords : loadingOvertimeByPayrunMonth}
          rowSelection={rowSelection}
          pagination={false}
          scroll={{ x: 'max-content' }}
          rowClassName={(record: any) => (record.disabled ? 'table-row-disabled' : '')}
          onRow={(record: any) => ({
            onClick: () => handleView(record),
          })}
        />
      </div>

      <Modal
        title={t('overtime:assignPayrunMonthTitle')}
        open={isAssignModalVisible}
        onOk={handleAssignModalOk}
        onCancel={handleAssignModalCancel}
        confirmLoading={payrunMonthUpdateLoading}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <strong>{t('overtime:selectedRecords')}:</strong> {assignSelectedRecords.length}
          </div>
          <div>
            <Space wrap>
              {assignSelectedRecords.map((record: any) => (
                <Tag key={record.id} color="blue">
                  {record.name || record.title}
                </Tag>
              ))}
            </Space>
          </div>
          <div>
            <DatePicker
              picker="month"
              value={
                moment(assignPayrunMonth, 'YYYY-MM', true).isValid()
                  ? moment(assignPayrunMonth, 'YYYY-MM')
                  : null
              }
              format="YYYY-MM"
              onChange={(date, dateString) => {
                if (dateString) {
                  setAssignPayrunMonth(dateString);
                } else {
                  setAssignPayrunMonth('');
                }
              }}
              style={{ width: '100%' }}
            />
          </div>
        </Space>
      </Modal>

      {/* Edit Payrun Month Modal */}
      <Modal
        title={t('overtime:editPayrunMonth')}
        open={isEditModalVisible}
        onOk={handleSavePayrunMonth}
        onCancel={handleCancelEdit}
        confirmLoading={payrunMonthUpdateLoading}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item label={t('overtime:employeeCode')} name="employeeCode">
            <Input disabled />
          </Form.Item>

          <Form.Item label={t('overtime:name')} name="name">
            <Input disabled />
          </Form.Item>

          <Form.Item
            label={t('overtime:payrunMonth')}
            name="payrunMonth"
            rules={[
              { required: true, message: t('overtime:payrunMonthRequired') },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();

                  const currentDate = new Date();
                  const selectedDate = new Date(value);
                  const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth());

                  // Check if not too far in the future (max 12 months)
                  const maxFutureDate = new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth() + 12,
                  );
                  if (selectedDate > maxFutureDate) {
                    return Promise.reject(
                      'Cannot assign to payrun month more than 12 months in the future',
                    );
                  }

                  // Check if not too far in the past (max 24 months)
                  const maxPastDate = new Date(
                    currentDate.getFullYear(),
                    currentDate.getMonth() - 24,
                  );
                  if (selectedDate < maxPastDate) {
                    return Promise.reject(
                      'Cannot assign to payrun month more than 24 months in the past',
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <DatePicker picker="month" format="YYYY-MM" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label={t('overtime:totalHour')} name="totalHour">
            <Input disabled />
          </Form.Item>

          <Form.Item
            label={t('overtime:transferHours')}
            name="transferHours"
            rules={[
              { required: true, message: t('overtime:transferHoursRequired') },
              {
                validator: (_, value) => {
                  if (value < 0) {
                    return Promise.reject('Transfer hours cannot be negative');
                  }
                  if (value === 0) {
                    return Promise.reject(
                      'Transfer hours cannot be zero. Leave empty to transfer all hours.',
                    );
                  }
                  if (value < 0.01) {
                    return Promise.reject('Transfer hours must be at least 0.01 hours');
                  }
                  if (currentItem && value > currentItem.totalHour) {
                    return Promise.reject(
                      `Transfer hours cannot exceed total hours (${currentItem.totalHour})`,
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              type="number"
              min={0.01}
              step={0.01}
              placeholder="Enter hours to transfer (leave empty for all hours)"
            />
          </Form.Item>

          <Form.Item label={t('overtime:salaryCoefficient')} name="salaryCoefficient">
            <Select placeholder={t('overtime:selectSalaryCoefficient')} allowClear>
              {allSalaryCoefficients.map((item: any) => (
                <Select.Option key={item.coefficient} value={item.coefficient.toString()}>
                  {item.coefficient} {item.timeFrame ? `(${item.timeFrame})` : ''}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </ContainerMain>
  );
};

export default OvertimePayrunManagement;
